import { QuestionOption } from "../interfaces/questionOption";
import { Questions } from "../interfaces/questions";
import { CandidateQuestionnaire } from "../interfaces/candidateQuestionnaire";
import { fetchWithAuth } from "./fetchWithAuth";
import { ReferenceQuestionnaire } from "../interfaces/referenceQuestionnaire";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

// This will create a candidate questionnaire record with base text, questions, question options, and statement of applicant
export const createCandidateQuestionnaire = async (
  clientID: number,
  fiveYear?: boolean,
  diocese?: string,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const formData = {
    clientID,
    fiveYear,
    diocese,
  };

  const response = await fetch(
    `${apiBaseUrl}/api/CandidateQuestionnaire/CreateCandidateQuestionnaire`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    },
  );

  if (!response.ok) {
    throw new Error(
      "Failed to create the candidate questionnaire for the new client.",
    );
  }
};

export const getCandidateQuestionnaireBase = async (
  clientID: number,
  fiveYear: boolean,
): Promise<CandidateQuestionnaire> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CandidateQuestionnaire/GetCandidateQuestionnaireBase/${clientID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ fiveYear }),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch Candidate Questionnaire Base.");
  }

  return await response.json();
};

export const getCandidateQuestionnaireQuestions = async (
  candidateQuestionnaireID: number,
): Promise<Questions[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CandidateQuestionnaire/GetQuestions/${candidateQuestionnaireID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      "Failed to fetch questions for the Candidate Questionnaire.",
    );
  }

  return await response.json();
};

export const getCandidateQuestionnaireQuestionOptions = async (
  questionID: number,
): Promise<QuestionOption[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CandidateQuestionnaire/GetQuestionOptions/${questionID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch question options for the question.");
  }

  return await response.json();
};

export const updateBaseTextCandidate = async (
  clientID: number,
  baseTextKey: keyof ReferenceQuestionnaire,
  baseText: string,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    clientID,
    baseTextKey,
    baseText,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/CandidateQuestionnaire/UpdateBaseText`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update the base text.");
    }
  } catch (error) {
    console.error("Error updating base text:", error);
    throw error;
  }
};

export const updateCandidateQuestion = async (
  questionID: number,
  text: string,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    questionID,
    text,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/CandidateQuestionnaire/UpdateQuestion`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update the question.");
    }
  } catch (error) {
    console.error("Error updating question:", error);
    throw error;
  }
};

export const updateCandidateOption = async (
  optionID: number,
  text: string,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    optionID,
    text,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/CandidateQuestionnaire/UpdateOption`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update the option.");
    }
  } catch (error) {
    console.error("Error updating option:", error);
    throw error;
  }
};

export const deleteCandidateQuestion = async (
  questionID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    questionID,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/CandidateQuestionnaire/DeleteQuestion`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to delete the question.");
    }
  } catch (error) {
    console.error("Error deleting question:", error);
    throw error;
  }
};

export const deleteCandidateOption = async (
  optionID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    optionID,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/CandidateQuestionnaire/DeleteOption`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to delete the option.");
    }
  } catch (error) {
    console.error("Error deleting option:", error);
    throw error;
  }
};

export const createCandidateQuestionWithOptions = async (
  questionnaireID: number,
  questionText: string,
  options: string[],
): Promise<any> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const formData = {
    candidateQuestionnaireID: questionnaireID,
    questionText,
    options,
  };

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CandidateQuestionnaire/CreateCandidateQuestionWithOptions`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(formData),
    },
  );

  if (!response.ok) {
    throw new Error("Error creating question");
  }

  return response.json();
};
